import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Coffee, ArrowRight, Copy, Loader2, Landmark, LogIn } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";

const QUICK = [5, 10, 25, 50, 100];
export const MUG_THRESHOLD = 100;
const FIELDS = [["holder", "Kontoinhaber"], ["iban", "IBAN"], ["bic", "BIC"], ["bank", "Bank"], ["code", "Verwendungszweck (dein persönlicher Code)"]];

export function useBankDetails() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!user) return undefined;
    let on = true;
    api.get("/support").then(({ data }) => on && setD(data)).catch(() => on && setD({}));
    return () => { on = false; };
  }, [user]);
  return user ? d : false;
}

const copy = (v, k, l) => { navigator.clipboard?.writeText(k === "iban" ? v.replace(/\s/g, "") : v); toast.success(`${l.split(" (")[0]} kopiert`); };

const giroPayload = (d, amount) => ["BCD", "002", "1", "SCT", (d.bic || "").replace(/\s/g, ""), d.holder.slice(0, 70), d.iban.replace(/\s/g, ""), amount > 0 ? `EUR${Number(amount).toFixed(2)}` : "", "", "", d.code, ""].join("\n");

function GiroQR({ d, amount, lux, testId }) {
  if (!d.holder || !d.iban) return null;
  return (
    <div className={`rounded-2xl p-3 flex items-center gap-4 ${lux ? "bg-white/10" : "bg-white ring-1 ring-pink-100"}`} data-testid={`${testId}-qr`}>
      <span className="shrink-0 rounded-xl bg-white p-2"><QRCodeSVG value={giroPayload(d, amount)} size={112} level="M" data-testid={`${testId}-qr-code`} /></span>
      <div className={`text-xs space-y-1 ${lux ? "text-rose-50" : "text-slate-600"}`}>
        <p className={`font-extrabold text-sm ${lux ? "text-white" : "text-slate-800"}`}>Mit der Banking-App scannen</p>
        <p>Empfänger, IBAN, {amount > 0 ? <b data-testid={`${testId}-qr-amount`}>{String(amount).replace(".", ",")} €</b> : "Betrag"} und dein Code <b className="font-mono">{d.code}</b> sind schon ausgefüllt.</p>
        <p className="opacity-70">Funktioniert mit allen Apps, die GiroCode / SEPA-QR unterstützen.</p>
      </div>
    </div>
  );
}

export function BankDetails({ lux = false, testId = "support", amount = 0 }) {
  const d = useBankDetails();
  const muted = lux ? "text-rose-100/70" : "text-slate-400";
  if (d === false) return (
    <div className={`rounded-2xl p-4 text-sm flex flex-wrap items-center gap-3 ${lux ? "bg-white/10 text-rose-50" : "bg-rose-50 text-slate-600"}`} data-testid={`${testId}-login-hint`}>
      <LogIn className="w-5 h-5 shrink-0" /> <span className="flex-1 min-w-[10rem]">Die Kontodaten sehen nur eingeloggte Mitglieder.</span>
      <Link to="/login" className="h-9 px-4 rounded-full pink-gradient text-white text-xs font-bold inline-flex items-center" data-testid={`${testId}-login-btn`}>Einloggen</Link>
    </div>
  );
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-rose-500 mx-auto" />;
  if (!d.iban) return <p className={`text-sm text-center py-4 ${muted}`} data-testid={`${testId}-no-bank`}>Die Kontodaten werden in Kürze hier ergänzt.</p>;
  return (
    <div className="space-y-3">
    <GiroQR d={d} amount={amount} lux={lux} testId={testId} />
    <ul className={`rounded-2xl divide-y ${lux ? "bg-white/10 divide-white/10" : "bg-white divide-pink-50"}`} data-testid={`${testId}-bank-details`}>
      {FIELDS.filter(([k]) => d[k]).map(([k, l]) => (
        <li key={k} className={`flex items-center gap-3 px-3 py-2.5 ${k === "code" ? (lux ? "bg-amber-300/15" : "bg-amber-50") : ""}`}>
          <div className="flex-1 min-w-0">
            <p className={`text-[11px] ${muted}`}>{l}</p>
            <p className={`font-semibold break-all ${k === "code" ? "font-mono text-base tracking-wider" : "text-sm"} ${lux ? "text-white" : "text-slate-800"}`} data-testid={`${testId}-${k}`}>{d[k]}</p>
          </div>
          <button type="button" onClick={() => copy(d[k], k, l)} data-testid={`${testId}-copy-${k}`} aria-label={`${l} kopieren`} className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${lux ? "bg-white/10 text-rose-100 hover:bg-white/20" : "bg-rose-50 text-rose-500 hover:bg-rose-100"}`}><Copy className="w-4 h-4" /></button>
        </li>
      ))}
    </ul>
    </div>
  );
}

export function MugHint({ value, lux = false, testId }) {
  const on = value >= MUG_THRESHOLD;
  const cls = on
    ? (lux ? "vip-gold-metal shadow-lg shadow-amber-500/30" : "bg-gradient-to-r from-amber-300 to-yellow-200 text-amber-950 shadow-lg shadow-amber-400/30 ring-2 ring-amber-400")
    : (lux ? "bg-white/10 text-rose-100/90" : "bg-amber-50 text-amber-900/80");
  return (
    <div data-testid={testId} data-active={on} className={`rounded-2xl px-3 py-2.5 text-xs flex items-center gap-2 transition-[background-color,transform,box-shadow] duration-300 ${on ? "scale-[1.02] font-bold" : "font-semibold"} ${cls}`}>
      <Coffee className={`w-4 h-4 shrink-0 ${on ? "animate-bounce" : ""}`} />
      {on ? "Du bekommst unsere Dankeschön-Tasse! ☕" : `Ab ${MUG_THRESHOLD} € auf einmal schenken wir dir unsere Dankeschön-Tasse.`}
    </div>
  );
}

export function DonationReport({ value, lux = false, testId }) {
  const { user } = useAuth();
  const [reported, setReported] = useState(false);
  if (!user) return null;
  const report = async () => {
    try { await api.post("/donations/report", { amount: value }); setReported(true); toast.success("Danke von Herzen! 💖 Wir haben deine Meldung erhalten und prüfen den Eingang."); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-1.5">
      <button onClick={report} disabled={reported || !value} data-testid={testId} className={`text-xs font-bold underline-offset-2 hover:underline disabled:opacity-50 inline-flex items-center gap-1 ${lux ? "text-rose-100" : "text-rose-600"}`}>
        <Heart className="w-3.5 h-3.5" /> {reported ? "Danke, Meldung gesendet!" : `Ich habe ${value ? `${String(value).replace(".", ",")} € ` : ""}überwiesen – Bescheid geben`}
      </button>
      {reported && value >= MUG_THRESHOLD && (
        <>
          <p className={`text-[11px] ${lux ? "text-rose-100/80" : "text-rose-900/60"}`} data-testid={`${testId}-mug-note`}>Sobald wir den Eingang bestätigt haben, kannst du hier deine Adresse für die Tasse eintragen:</p>
          <Link to="/dankeschoen-tasse" data-testid={`${testId}-mug-link`} className={`text-xs font-bold inline-flex items-center gap-1 hover:underline underline-offset-2 ${lux ? "text-amber-200" : "text-amber-700"}`}>
            <Coffee className="w-3.5 h-3.5" /> Lieferadresse für deine Tasse angeben <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </>
      )}
    </div>
  );
}

export function BankDonate({ lux = false, testId = "bank-donate" }) {
  const [amount, setAmount] = useState("10");
  const value = Math.round(Math.max(0, Number(String(amount).replace(",", ".")) || 0) * 100) / 100;
  const chip = (a) => `h-9 px-4 rounded-full text-sm font-bold transition-colors ${value === a ? (lux ? "vip-gold-metal" : "pink-gradient text-white") : (lux ? "bg-white/10 text-rose-50 hover:bg-white/20" : "bg-white text-rose-600 hover:bg-rose-50")}`;
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className={`text-sm font-bold flex items-center gap-2 ${lux ? "text-white" : "text-slate-800"}`}><Landmark className="w-4 h-4" /> Per Banküberweisung unterstützen</p>
      <div className="flex flex-wrap gap-2">
        {QUICK.map((a) => <button key={a} onClick={() => setAmount(String(a))} data-testid={`${testId}-quick-${a}`} className={chip(a)}>{a} €</button>)}
        <label className={`h-9 pl-3 pr-3 rounded-full flex items-center gap-1 text-sm font-bold ${lux ? "bg-white/10 text-rose-50" : "bg-white text-rose-600"}`}>
          <span className="text-[11px] font-semibold opacity-60">Freier Betrag</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9,.]/g, "").slice(0, 7))} inputMode="decimal" aria-label="Freier Betrag" placeholder="Betrag" data-testid={`${testId}-amount`} className="w-16 bg-transparent outline-none text-right placeholder:text-current placeholder:opacity-50" /> €
        </label>
      </div>
      <MugHint value={value} lux={lux} testId={`${testId}-mug-hint`} />
      <BankDetails lux={lux} testId={`${testId}-bank`} amount={value} />
      <p className={`text-xs flex gap-1.5 ${lux ? "text-rose-100/90" : "text-rose-900/70"}`}><Heart className="w-3.5 h-3.5 shrink-0 mt-0.5" fill="currentColor" /> Gib unbedingt deinen persönlichen Code als Verwendungszweck an. Ab 25 € schalten wir dich als Dankeschön 7 Tage als Projekt Unterstützer frei.</p>
      <DonationReport value={value} lux={lux} testId={`${testId}-report`} />
    </div>
  );
}
